/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, RefreshCw, Save, Copy, Check, Image as ImageIcon,
  Video, LayoutGrid, Wand2, ExternalLink, Trash2, AlertCircle, Send,
  CheckCircle2, Link2, BarChart3, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { pinterestAPI } from '../lib/api';

const PIN_TYPES = [
  { key: 'hero', label: 'Hero Pin', icon: ImageIcon, picker: 'single', accepts: 'photo',
    hint: 'One clean product photo. Front-loaded keyword title.' },
  { key: 'lifestyle', label: 'Lifestyle Pin', icon: ImageIcon, picker: 'single', accepts: 'photo',
    hint: 'Product styled / worn / in context. Aesthetic-leaning copy.' },
  { key: 'mood_board', label: 'Mood Board Pin', icon: LayoutGrid, picker: 'multi', accepts: 'photo',
    hint: 'A curated collage. Choose a layout — or let OpenAI synthesize one.' },
  { key: 'video', label: 'Video Pin', icon: Video, picker: 'single', accepts: 'video',
    hint: 'Short-form motion of the product. Hook-first title.' },
  { key: 'carousel', label: 'Carousel Pin', icon: LayoutGrid, picker: 'multi', accepts: 'photo',
    hint: '2-5 product photos. One title/description across all slides.' },
];

const MOOD_LAYOUTS = [
  { key: '2x2', label: '2 × 2 grid', hint: '4 equal squares' },
  { key: 'hero_thumbs', label: 'Hero + 3 thumbs', hint: '1 large + 3 smaller' },
  { key: 'vertical', label: 'Vertical strip', hint: '2-3 stacked' },
  { key: 'ai', label: 'AI generate (OpenAI)', hint: 'gpt-image-2 synthesizes a fresh collage' },
];

export default function PinterestPinsPanel({ product }) {
  const [oauthStatus, setOauthStatus] = useState(null);
  const [pinsByType, setPinsByType] = useState({});
  const [activeType, setActiveType] = useState('hero');
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingMoodImage, setGeneratingMoodImage] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(null);

  // Per-type media selection state (mid arrays)
  const [selections, setSelections] = useState({});
  const [moodLayout, setMoodLayout] = useState('2x2');
  const [moodEraHint, setMoodEraHint] = useState('');

  // URL inputs — shared across pin types
  const [etsyUrl, setEtsyUrl] = useState(product.input_url || '');
  const [tljUrl, setTljUrl] = useState(product.tlj_published_url || '');
  const [customUrl, setCustomUrl] = useState('');

  const productPhotos = useMemo(
    () => (product.media || []).filter((m) => m.kind === 'photo'),
    [product.media],
  );
  const productVideos = useMemo(
    () => (product.media || []).filter((m) => m.kind === 'video'),
    [product.media],
  );

  const load = useCallback(async () => {
    try {
      const [statusRes, pinsRes] = await Promise.all([
        pinterestAPI.oauthStatus(),
        pinterestAPI.listProductPins(product.id),
      ]);
      setOauthStatus(statusRes.data);
      const pins = pinsRes.data.pins || {};
      setPinsByType(pins);
      // Seed selection + URL state from saved drafts
      const sels = {};
      let seededEtsy = '';
      let seededTlj = '';
      let seededCustom = '';
      Object.entries(pins).forEach(([t, p]) => {
        if (p?.selected_media_ids) sels[t] = p.selected_media_ids;
        if (p?.etsy_url && !seededEtsy) seededEtsy = p.etsy_url;
        if (p?.tlj_url && !seededTlj) seededTlj = p.tlj_url;
        if (p?.custom_url && !seededCustom) seededCustom = p.custom_url;
        if (t === 'mood_board' && p?.mood_board_layout) setMoodLayout(p.mood_board_layout);
        if (t === 'mood_board' && p?.era_mood_hint) setMoodEraHint(p.era_mood_hint);
      });
      setSelections((prev) => ({ ...prev, ...sels }));
      if (seededEtsy) setEtsyUrl(seededEtsy);
      if (seededTlj) setTljUrl(seededTlj);
      if (seededCustom) setCustomUrl(seededCustom);
    } catch (err) {
      console.error('Failed to load Pinterest state', err);
      toast.error('Failed to load Pinterest panel');
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  const loadBoards = useCallback(async () => {
    if (!oauthStatus?.connected) return;
    setBoardsLoading(true);
    try {
      const res = await pinterestAPI.listBoards();
      setBoards(res.data.boards || []);
    } catch (err) {
      console.error('Failed to load boards', err);
    } finally {
      setBoardsLoading(false);
    }
  }, [oauthStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBoards(); }, [loadBoards]);

  const handleConnect = async () => {
    try {
      const res = await pinterestAPI.oauthStart();
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start OAuth');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Pinterest?')) return;
    try {
      await pinterestAPI.oauthDisconnect();
      toast.success('Pinterest disconnected');
      load();
    } catch {
      toast.error('Disconnect failed');
    }
  };

  const toggleMedia = (mid, picker) => {
    setSelections((prev) => {
      const cur = prev[activeType] || [];
      if (picker === 'single') {
        return { ...prev, [activeType]: cur.includes(mid) ? [] : [mid] };
      }
      const set = new Set(cur);
      if (set.has(mid)) set.delete(mid); else set.add(mid);
      return { ...prev, [activeType]: Array.from(set) };
    });
  };

  const handleGenerate = async () => {
    const ids = selections[activeType] || [];
    const pt = PIN_TYPES.find((p) => p.key === activeType);
    if (pt.picker === 'single' && ids.length === 0) {
      toast.error('Pick one photo first');
      return;
    }
    if (activeType === 'carousel' && ids.length < 2) {
      toast.error('Carousel needs at least 2 images');
      return;
    }
    setGenerating(true);
    try {
      const res = await pinterestAPI.generatePin(product.id, activeType, {
        selected_media_ids: ids,
      });
      setPinsByType((prev) => ({ ...prev, [activeType]: res.data }));
      toast.success(`${pt.label} metadata generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    try {
      const res = await pinterestAPI.generateAllPins(product.id, selections);
      const next = { ...pinsByType, ...res.data.pins };
      setPinsByType(next);
      const errs = res.data.errors || {};
      const errCount = Object.keys(errs).length;
      const okCount = Object.keys(res.data.pins || {}).length;
      if (okCount > 0) toast.success(`${okCount} pin type${okCount > 1 ? 's' : ''} generated`);
      if (errCount > 0) {
        toast.warning(
          `Skipped: ${Object.entries(errs).map(([t, e]) => `${t}(${e})`).join(', ')}`,
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generate-all failed');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleGenerateMoodImage = async () => {
    const ids = selections.mood_board || [];
    if (moodLayout !== 'ai' && ids.length < 2) {
      toast.error('Pick at least 2 photos for the collage');
      return;
    }
    setGeneratingMoodImage(true);
    try {
      const res = await pinterestAPI.generateMoodBoardImage(product.id, {
        layout: moodLayout,
        selected_media_ids: ids,
        era_mood_hint: moodLayout === 'ai' ? (moodEraHint || undefined) : undefined,
      });
      setPinsByType((prev) => ({ ...prev, mood_board: res.data }));
      if (moodLayout === 'ai' && res.data?.mood_board_status === 'pending') {
        toast.info('Generating 4-cell mood board… takes ~2 minutes', { duration: 4000 });
      } else {
        toast.success('Mood board image ready');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mood board failed');
    } finally {
      setGeneratingMoodImage(false);
    }
  };

  // Poll while the AI mood board is generating in the background
  // (runs ~120-180s, must not block the HTTP request thread).
  useEffect(() => {
    const status = pinsByType?.mood_board?.mood_board_status;
    if (status !== 'pending') return undefined;
    const t = setInterval(async () => {
      try {
        const res = await pinterestAPI.listProductPins(product.id);
        const nextMb = (res.data?.pins || {}).mood_board;
        if (nextMb) {
          setPinsByType((prev) => ({ ...prev, mood_board: nextMb }));
          if (nextMb.mood_board_status === 'ready') {
            toast.success('Mood board image ready');
          } else if (nextMb.mood_board_status === 'failed') {
            toast.error(nextMb.mood_board_error || 'Mood board failed');
          }
        }
      } catch (e) {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [pinsByType?.mood_board?.mood_board_status, product.id]);

  const patchActivePin = (changes) => {
    setPinsByType((prev) => {
      const cur = prev[activeType] || {};
      return { ...prev, [activeType]: { ...cur, ...changes } };
    });
  };

  const savePin = async (extra = {}) => {
    const pin = pinsByType[activeType];
    if (!pin) {
      toast.error('Generate metadata first');
      return;
    }
    const payload = {
      title: pin.title || '',
      description: pin.description || '',
      alt_text: pin.alt_text || '',
      board_name: pin.board_name || '',
      keywords: pin.keywords || [],
      destination_choice: pin.destination_choice || 'etsy',
      include_utm: pin.include_utm ?? true,
      etsy_url: etsyUrl,
      tlj_url: tljUrl,
      custom_url: customUrl,
      pinterest_board_id: pin.pinterest_board_id || null,
      ...extra,
    };
    try {
      const res = await pinterestAPI.patchPin(product.id, activeType, payload);
      setPinsByType((prev) => ({ ...prev, [activeType]: res.data }));
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handlePublish = async () => {
    const pin = pinsByType[activeType];
    if (!pin) return;
    if (!pin.title || !pin.description) {
      toast.error('Title and description required');
      return;
    }
    setPublishing(true);
    try {
      // Always save the latest edits first
      await savePin();
      const res = await pinterestAPI.publishPin(product.id, activeType, {
        etsy_url: etsyUrl,
        tlj_url: tljUrl,
        custom_url: customUrl,
      });
      if (res.data.status === 'pending_api_access') {
        toast.info('Saved — will publish once Pinterest API access is granted');
      } else if (res.data.status === 'published') {
        toast.success('Published to Pinterest');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const copy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
    toast.success('Copied');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  const activeDef = PIN_TYPES.find((p) => p.key === activeType);
  const activePin = pinsByType[activeType];
  const activeIds = selections[activeType] || [];
  const apiPending = !oauthStatus?.configured;

  return (
    <div className="space-y-6" data-testid="pinterest-panel">
      {/* Connection card */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="pinterest-connection-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600 font-bold">P</div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">Pinterest publishing</h3>
              {apiPending ? (
                <p className="text-xs text-zinc-500 mt-0.5 max-w-md">
                  Pinterest API access pending approval. Drafts save locally; the second your app is approved we publish automatically.
                </p>
              ) : oauthStatus?.connected ? (
                <p className="text-xs text-zinc-500 mt-0.5">
                  Connected as <span className="font-semibold">{oauthStatus.account?.pinterest_username || 'Pinterest user'}</span>
                </p>
              ) : (
                <p className="text-xs text-zinc-500 mt-0.5">Connect your Pinterest business account to publish.</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {apiPending ? (
              <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wider">
                Pending API access
              </Badge>
            ) : oauthStatus?.connected ? (
              <Button size="sm" variant="outline" onClick={handleDisconnect} data-testid="pinterest-disconnect-btn">
                Disconnect
              </Button>
            ) : (
              <Button size="sm" onClick={handleConnect} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pinterest-connect-btn">
                Connect Pinterest
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Destination URLs */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="pinterest-urls-card">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1.5">
            <Link2 size={14} className="text-rose-600" /> Destination URLs
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Enter both URLs; for each pin pick which one Pinterest sends shoppers to.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Etsy URL</label>
            <Input
              value={etsyUrl}
              onChange={(e) => setEtsyUrl(e.target.value)}
              placeholder="https://www.etsy.com/listing/..."
              className="font-mono text-xs"
              data-testid="pinterest-etsy-url-input"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">TheLocalJewel URL</label>
            <Input
              value={tljUrl}
              onChange={(e) => setTljUrl(e.target.value)}
              placeholder="https://thelocaljewel.com/products/..."
              className="font-mono text-xs"
              data-testid="pinterest-tlj-url-input"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Custom URL (optional)</label>
            <Input
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://..."
              className="font-mono text-xs"
              data-testid="pinterest-custom-url-input"
            />
          </div>
        </div>
      </div>

      {/* Pin type tabs */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="pinterest-type-tabs">
        <div className="flex flex-wrap border-b border-zinc-200 bg-zinc-50/60">
          {PIN_TYPES.map((pt) => {
            const Icon = pt.icon;
            const has = !!pinsByType[pt.key]?.title;
            const isActive = activeType === pt.key;
            return (
              <button
                key={pt.key}
                onClick={() => setActiveType(pt.key)}
                className={`flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium border-r border-zinc-200 last:border-r-0 transition-colors ${
                  isActive ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:bg-white hover:text-zinc-800'
                }`}
                data-testid={`pinterest-tab-${pt.key}`}
              >
                <Icon size={13} className={isActive ? 'text-rose-600' : ''} />
                {pt.label}
                {has && <Check size={11} className="text-emerald-600 ml-0.5" />}
              </button>
            );
          })}
        </div>

        <div className="p-5 space-y-5">
          {/* Type hint + Generate buttons */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-xs text-zinc-500 max-w-lg">{activeDef.hint}</p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateAll}
                disabled={generatingAll || generating}
                data-testid="pinterest-generate-all-btn"
              >
                {generatingAll
                  ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Generating all…</>
                  : <><Wand2 size={12} className="mr-1.5" /> Generate all 5</>}
              </Button>
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={generating || generatingAll}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid={`pinterest-generate-${activeType}-btn`}
              >
                {generating
                  ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Generating…</>
                  : <><Sparkles size={12} className="mr-1.5" /> Generate {activeDef.label}</>}
              </Button>
            </div>
          </div>

          {/* Media picker */}
          <div className="space-y-2" data-testid={`pinterest-media-picker-${activeType}`}>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                {activeDef.accepts === 'video' ? 'Pick a video' : `Pick ${activeDef.picker === 'multi' ? '2-5 photos' : '1 photo'}`}
              </label>
              <span className="text-[10px] text-zinc-400">{activeIds.length} selected</span>
            </div>
            {(activeDef.accepts === 'video' ? productVideos : productPhotos).length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-md p-6 text-center text-xs text-zinc-400">
                No {activeDef.accepts}s yet — upload media in the section below first.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {(activeDef.accepts === 'video' ? productVideos : productPhotos).map((m) => {
                  const selected = activeIds.includes(m.id);
                  const order = selected ? activeIds.indexOf(m.id) + 1 : null;
                  return (
                    <button
                      key={m.id}
                      onClick={() => toggleMedia(m.id, activeDef.picker)}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${
                        selected ? 'border-rose-500 ring-2 ring-rose-200' : 'border-zinc-200 hover:border-zinc-400'
                      }`}
                      data-testid={`pinterest-media-${m.id}`}
                    >
                      {m.kind === 'video' ? (
                        <div className="w-full h-full bg-zinc-900 flex items-center justify-center text-white">
                          <Video size={20} />
                        </div>
                      ) : (
                        <img src={m.view_url} alt={m.display_name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                      {selected && (
                        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {activeDef.picker === 'multi' ? order : <Check size={12} />}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mood board layout + image */}
          {activeType === 'mood_board' && (
            <div className="bg-rose-50/30 border border-rose-100 rounded-md p-4 space-y-3" data-testid="pinterest-moodboard-controls">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 mb-2">Mood board layout</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {MOOD_LAYOUTS.map((l) => (
                    <button
                      key={l.key}
                      onClick={() => setMoodLayout(l.key)}
                      className={`text-left rounded-md border p-2.5 transition-colors ${
                        moodLayout === l.key ? 'border-rose-500 bg-white' : 'border-zinc-200 bg-white hover:border-zinc-400'
                      }`}
                      data-testid={`mood-layout-${l.key}`}
                    >
                      <div className="text-xs font-semibold text-zinc-900">{l.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{l.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
              {moodLayout === 'ai' && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 mb-1">
                    Era / mood hint <span className="text-zinc-400 font-normal normal-case">(optional)</span>
                  </div>
                  <input
                    type="text"
                    value={moodEraHint}
                    onChange={(e) => setMoodEraHint(e.target.value)}
                    placeholder='e.g. "vintage Art Deco" · "old Hollywood bride" · "modern Bauhaus minimalist"'
                    className="w-full h-9 px-3 text-[13px] bg-white border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-200"
                    data-testid="pinterest-mood-era-hint"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                    AI will produce a 4-cell board: Color · Era · Simpler ring silhouette · Detail texture. Use the hint to lean the board toward a specific era or lifestyle.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerateMoodImage}
                  disabled={generatingMoodImage || activePin?.mood_board_status === 'pending'}
                  data-testid="pinterest-mood-build-btn"
                >
                  {(generatingMoodImage || activePin?.mood_board_status === 'pending')
                    ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> {activePin?.mood_board_status === 'pending' ? 'Generating (~2 min)…' : 'Building…'}</>
                    : <><LayoutGrid size={12} className="mr-1.5" /> {activePin?.mood_board_image_url ? 'Rebuild image' : 'Build image'}</>}
                </Button>
                {activePin?.mood_board_image_url && activePin?.mood_board_status !== 'pending' && (
                  <div className="flex items-center gap-3 text-xs">
                    <a
                      href={activePin.mood_board_image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                      data-testid="pinterest-mood-preview-link"
                    >
                      Preview full size <ExternalLink size={11} />
                    </a>
                    <a
                      href={activePin.mood_board_download_url || activePin.mood_board_image_url}
                      download={`pinterest-mood-board-${product.id}.png`}
                      className="text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                      data-testid="pinterest-mood-download-link"
                    >
                      <Download size={11} /> Download PNG
                    </a>
                  </div>
                )}
              </div>
              {activePin?.mood_board_status === 'pending' && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900">
                  <Loader2 size={14} className="animate-spin flex-shrink-0 mt-0.5" />
                  <span>
                    Claude is imagining a 4-cell mood board and gpt-image-2 is rendering each cell in parallel — this usually takes 90-180 seconds. You can keep working in other tabs; we&apos;ll keep checking and update this card automatically when it&apos;s ready.
                  </span>
                </div>
              )}
              {activePin?.mood_board_status === 'failed' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{activePin.mood_board_error || 'Mood board generation failed. Click Rebuild to retry.'}</span>
                </div>
              )}
              {activePin?.mood_board_image_url && activePin?.mood_board_status !== 'pending' && (
                <div className="rounded-md overflow-hidden border border-zinc-200 max-w-xs">
                  <img src={activePin.mood_board_image_url} alt="Mood board preview" className="w-full" />
                </div>
              )}
            </div>
          )}

          {/* Metadata editor */}
          {activePin ? (
            <div className="space-y-4" data-testid={`pinterest-meta-${activeType}`}>
              {activePin.parse_failed && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>AI output couldn&apos;t be parsed cleanly — fields may be empty. Try Regenerate.</span>
                </div>
              )}

              <FieldBlock
                label="Pin title"
                hint={`${(activePin.title || '').length}/100 chars`}
                hintWarn={(activePin.title || '').length > 100}
                value={activePin.title || ''}
                onChange={(v) => patchActivePin({ title: v })}
                copyKey={`title-${activeType}`}
                copied={copied}
                onCopy={copy}
                testid="pinterest-title-input"
              />

              <FieldBlock
                label="Pin description"
                hint={`${(activePin.description || '').length}/800 chars`}
                hintWarn={(activePin.description || '').length > 800}
                value={activePin.description || ''}
                onChange={(v) => patchActivePin({ description: v })}
                textarea
                rows={5}
                copyKey={`desc-${activeType}`}
                copied={copied}
                onCopy={copy}
                testid="pinterest-description-input"
              />

              <FieldBlock
                label="Alt text"
                hint={`${(activePin.alt_text || '').length} chars`}
                value={activePin.alt_text || ''}
                onChange={(v) => patchActivePin({ alt_text: v })}
                textarea
                rows={2}
                copyKey={`alt-${activeType}`}
                copied={copied}
                onCopy={copy}
                testid="pinterest-alt-input"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldBlock
                  label="Recommended board"
                  value={activePin.board_name || ''}
                  onChange={(v) => patchActivePin({ board_name: v })}
                  copyKey={`board-${activeType}`}
                  copied={copied}
                  onCopy={copy}
                  testid="pinterest-board-input"
                />
                {oauthStatus?.connected && boards.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                      Map to Pinterest board {boardsLoading && <Loader2 size={10} className="inline animate-spin ml-1" />}
                    </label>
                    <select
                      value={activePin.pinterest_board_id || ''}
                      onChange={(e) => patchActivePin({ pinterest_board_id: e.target.value })}
                      className="w-full text-xs border border-zinc-200 rounded-md p-2"
                      data-testid="pinterest-board-id-select"
                    >
                      <option value="">— pick a board —</option>
                      {boards.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {activePin.alternate_board_names?.length > 0 && (
                <div className="text-xs text-zinc-500">
                  <span className="font-semibold">Alternate boards: </span>
                  {activePin.alternate_board_names.join(' · ')}
                </div>
              )}

              {activePin.keywords?.length > 0 && (
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Keywords</label>
                  <div className="flex flex-wrap gap-1.5">
                    {activePin.keywords.map((k, i) => (
                      <Badge key={i} className="bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px] font-normal">{k}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Destination toggles */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Destination for this pin</div>
                <div className="flex flex-wrap gap-3 text-xs">
                  {[
                    { key: 'etsy', label: 'Etsy URL' },
                    { key: 'tlj', label: 'TheLocalJewel URL' },
                    { key: 'custom', label: 'Custom URL' },
                  ].map((d) => (
                    <label key={d.key} className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`dest-${activeType}`}
                        checked={(activePin.destination_choice || 'etsy') === d.key}
                        onChange={() => patchActivePin({ destination_choice: d.key })}
                        data-testid={`pinterest-dest-${d.key}`}
                      />
                      {d.label}
                    </label>
                  ))}
                  <label className="inline-flex items-center gap-1.5 cursor-pointer ml-auto">
                    <input
                      type="checkbox"
                      checked={activePin.include_utm ?? true}
                      onChange={(e) => patchActivePin({ include_utm: e.target.checked })}
                      data-testid="pinterest-utm-toggle"
                    />
                    Append UTM params
                  </label>
                </div>
              </div>

              {/* Publish status banner */}
              {activePin.publish_status && activePin.publish_status !== 'draft' && (
                <div className={`flex items-center gap-2 rounded-md p-3 text-xs ${
                  activePin.publish_status === 'published'
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                    : activePin.publish_status === 'failed'
                      ? 'bg-red-50 border border-red-200 text-red-900'
                      : 'bg-amber-50 border border-amber-200 text-amber-900'
                }`}>
                  {activePin.publish_status === 'published' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                  <span>
                    {activePin.publish_status === 'published' && activePin.pinterest_url ? (
                      <>Published — <a href={activePin.pinterest_url} target="_blank" rel="noreferrer" className="underline">view on Pinterest</a></>
                    ) : activePin.publish_status === 'pending_api_access' ? (
                      'Saved — will publish once Pinterest API access is granted'
                    ) : activePin.publish_status === 'failed' ? (
                      `Publish failed: ${activePin.publish_error || 'unknown error'}`
                    ) : activePin.publish_status}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-zinc-100">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => savePin()}
                  data-testid="pinterest-save-draft-btn"
                >
                  <Save size={12} className="mr-1.5" /> Save draft
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGenerate}
                    disabled={generating}
                    data-testid="pinterest-regenerate-btn"
                  >
                    {generating ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <RefreshCw size={12} className="mr-1.5" />}
                    Regenerate
                  </Button>
                  <Button
                    size="sm"
                    onClick={handlePublish}
                    disabled={publishing}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="pinterest-publish-btn"
                  >
                    {publishing
                      ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Publishing…</>
                      : <><Send size={12} className="mr-1.5" /> {apiPending ? 'Save publish intent' : 'Publish to Pinterest'}</>}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-zinc-200 rounded-md p-8 text-center text-xs text-zinc-400">
              No draft yet. Pick media and click <span className="text-rose-700 font-semibold">Generate {activeDef.label}</span>.
            </div>
          )}

          {/* Analytics placeholder */}
          {activePin?.publish_status === 'published' && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-600 inline-flex items-center gap-2" data-testid="pinterest-analytics-hint">
              <BarChart3 size={14} className="text-zinc-500" />
              Analytics will populate after Pinterest collects ~24h of data — call
              <code className="mx-1 px-1 bg-white rounded border border-zinc-200 font-mono text-[10px]">GET /pinterest/etsy/{product.id}/pins/{activeType}/analytics</code>
              to view.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldBlock({ label, hint, hintWarn, value, onChange, textarea, rows = 3, copyKey, copied, onCopy, testid }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</label>
        <div className="flex items-center gap-2">
          {hint && <span className={`text-[10px] font-mono ${hintWarn ? 'text-red-600' : 'text-zinc-400'}`}>{hint}</span>}
          <button
            onClick={() => onCopy(value, copyKey)}
            className="text-zinc-400 hover:text-zinc-900"
            type="button"
            title={`Copy ${label}`}
          >
            {copied === copyKey ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
          </button>
        </div>
      </div>
      {textarea ? (
        <Textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm"
          data-testid={testid}
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}
